import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { toast } from '../../utils/notification';
import {
  UserPlusIcon,
  UserGroupIcon,
  EnvelopeIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const ROLE_OPTIONS = [
  { value: 'attorney', label: 'Attorney', color: 'blue' },
  { value: 'paralegal', label: 'Paralegal', color: 'purple' },
  { value: 'secretary', label: 'Secretary', color: 'teal' },
  { value: 'accountant', label: 'Accountant', color: 'emerald' },
  { value: 'intern', label: 'Intern', color: 'amber' },
  { value: 'admin', label: 'Admin', color: 'red' },
];

const ROLE_COLORS = {
  owner: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  attorney: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  paralegal: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  secretary: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  accountant: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  intern: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  client: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
};

const TeamManagement = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setPendingInvitations] = useState([]);
  const [orgRoles, setOrgRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('members');

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'attorney',
    org_role: '',
    first_name: '',
    last_name: '',
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  // Add user directly modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'attorney',
    org_role: '',
    password: '',
    phone: '',
  });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [createdUserInfo, setCreatedUserInfo] = useState(null);

  // Edit member modal
  const [editingMember, setEditingMember] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editOrgRole, setEditOrgRole] = useState('');
  const [editBillingEnabled, setEditBillingEnabled] = useState(false);
  const [editBillingRate, setEditBillingRate] = useState('');

  // Delete confirmation
  const [deletingMember, setDeletingMember] = useState(null);

  const isOrgAdmin = user?.role === 'owner' || user?.role === 'admin';

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [membersRes, invitationsRes, rolesRes] = await Promise.all([
        api.get('/api/accounts/organization/members/'),
        api.get('/api/accounts/organization/invitations/').catch(() => ({ data: [] })),
        api.get('/api/accounts/organization/roles/').catch(() => ({ data: [] })),
      ]);
      setMembers(membersRes.data?.results || membersRes.data || []);
      setPendingInvitations(invitationsRes.data?.results || invitationsRes.data || []);
      setOrgRoles(rolesRes.data?.results || rolesRes.data || []);
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) {
      setError('Email is required');
      return;
    }

    setInviteLoading(true);
    setError('');

    try {
      await api.post('/api/accounts/organization/invitations/', {
        email: inviteForm.email,
        role: inviteForm.role,
        org_role: inviteForm.org_role || undefined,
        first_name: inviteForm.first_name,
        last_name: inviteForm.last_name,
      });
      setSuccess(`Invitation sent to ${inviteForm.email}`);
      toast.success(`Invitation email sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'attorney', org_role: '', first_name: '', last_name: '' });
      fetchTeamData();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        (typeof err.response?.data === 'object'
          ? Object.values(err.response.data).flat().join('. ')
          : 'Failed to send invitation.');
      setError(msg);
      toast.error(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    if (!addUserForm.email || !addUserForm.first_name || !addUserForm.last_name) {
      setError('Email, first name, and last name are required');
      return;
    }

    setAddUserLoading(true);
    setError('');

    try {
      const payload = {
        email: addUserForm.email,
        first_name: addUserForm.first_name,
        last_name: addUserForm.last_name,
        role: addUserForm.role,
        phone: addUserForm.phone || undefined,
        org_role: addUserForm.org_role || undefined,
        password: addUserForm.password || undefined,
      };
      const res = await api.post('/api/accounts/organization/members/', payload);
      const tempPassword = res.data?.temporary_password;
      if (tempPassword) {
        setCreatedUserInfo({
          email: addUserForm.email,
          name: `${addUserForm.first_name} ${addUserForm.last_name}`,
          password: tempPassword,
        });
      }
      setSuccess(`User ${addUserForm.first_name} ${addUserForm.last_name} added successfully!`);
      toast.success(`${addUserForm.first_name} ${addUserForm.last_name} has been added and a welcome email was sent.`);
      setShowAddUserModal(false);
      setAddUserForm({ email: '', first_name: '', last_name: '', role: 'attorney', org_role: '', password: '', phone: '' });
      fetchTeamData();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.email?.[0] ||
        (typeof err.response?.data === 'object'
          ? Object.values(err.response.data).flat().join('. ')
          : 'Failed to add user.');
      setError(msg);
      toast.error(msg);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleUpdateMemberRole = async () => {
    if (!editingMember) return;

    try {
      const payload = {};
      if (editRole) payload.role = editRole;
      if (editOrgRole) payload.org_role = editOrgRole;
      // Per-member client billing (firm → client invoicing). Always send so a
      // firm admin can toggle billing off or clear the rate.
      payload.billing_enabled = editBillingEnabled;
      payload.billing_rate =
        editBillingEnabled && editBillingRate !== '' ? Number(editBillingRate) : null;

      await api.patch(`/api/accounts/organization/members/${editingMember.id}/`, payload);
      setSuccess(`Updated role for ${editingMember.first_name} ${editingMember.last_name}`);
      toast.success(`Role updated for ${editingMember.first_name} ${editingMember.last_name}`);
      setEditingMember(null);
      fetchTeamData();
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to update member role.';
      setError(errMsg);
      toast.error(errMsg);
    }
  };

  const handleRemoveMember = async () => {
    if (!deletingMember) return;

    try {
      await api.delete(`/api/accounts/organization/members/${deletingMember.id}/`);
      setSuccess(`${deletingMember.first_name} ${deletingMember.last_name} has been removed.`);
      toast.success(`${deletingMember.first_name} ${deletingMember.last_name} has been removed.`);
      setDeletingMember(null);
      fetchTeamData();
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to remove member.';
      setError(errMsg);
      toast.error(errMsg);
      setDeletingMember(null);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await api.delete(`/api/accounts/organization/invitations/${invitationId}/`);
      setSuccess('Invitation revoked.');
      toast.success('Invitation has been revoked.');
      fetchTeamData();
    } catch (err) {
      setError('Failed to revoke invitation.');
      toast.error('Failed to revoke invitation.');
    }
  };

  const filteredMembers = members.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.email?.toLowerCase().includes(q) ||
      m.first_name?.toLowerCase().includes(q) ||
      m.last_name?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <UserGroupIcon className="h-7 w-7 mr-2 text-primary-600" />
              Team Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your firm's lawyers, staff, and their roles.
              {user?.organization_name && (
                <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">
                  — {user.organization_name}
                </span>
              )}
            </p>
          </div>

          {isOrgAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddUserModal(true)}
                className="inline-flex items-center px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-sm"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Add User
              </button>
              <button
                onClick={() => setShowInviteModal(true)}
                className="inline-flex items-center px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-sm"
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Invite via Email
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-4 rounded-lg text-sm flex items-center justify-between">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
          <button onClick={() => setError('')}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300 p-4 rounded-lg text-sm flex items-center">
          <CheckIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2.5">
              <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{members.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active Members</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="bg-amber-100 dark:bg-amber-900/30 rounded-lg p-2.5">
              <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {invitations.filter(i => i.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending Invites</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2.5">
              <ShieldCheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{orgRoles.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Defined Roles</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('members')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'members'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <UserGroupIcon className="h-5 w-5 inline mr-1.5 -mt-0.5" />
              Members ({members.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'invitations'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <EnvelopeIcon className="h-5 w-5 inline mr-1.5 -mt-0.5" />
              Invitations ({invitations.length})
            </button>
          </nav>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="p-4">
            {/* Search */}
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <button
                onClick={fetchTeamData}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Refresh"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Members Table */}
            {filteredMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <UserGroupIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No team members found.</p>
                {isOrgAdmin && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Invite your first team member
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400">Member</th>
                      <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400">Role</th>
                      <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">Org Role</th>
                      <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400 hidden lg:table-cell">Billing</th>
                      <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell">Joined</th>
                      {isOrgAdmin && (
                        <th className="py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-right">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredMembers.map(member => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-sm mr-3">
                              {(member.first_name || '?')[0]}{(member.last_name || '?')[0]}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {member.first_name} {member.last_name}
                                {member.id === user?.id && (
                                  <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">(you)</span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[member.role] || ROLE_COLORS.client}`}>
                            {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden sm:table-cell">
                          <span className="text-gray-600 dark:text-gray-400 text-xs">
                            {member.org_role_name || '—'}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell text-xs">
                          {member.billing_enabled ? (
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              Billable
                              {member.billing_rate != null && member.billing_rate !== '' && (
                                <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">
                                  @ {parseFloat(member.billing_rate).toFixed(2)}/hr
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">Not billable</span>
                          )}
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell text-gray-500 dark:text-gray-400 text-xs">
                          {member.date_joined
                            ? new Date(member.date_joined).toLocaleDateString()
                            : '—'}
                        </td>
                        {isOrgAdmin && (
                          <td className="py-3 px-4 text-right">
                            {member.id !== user?.id && member.role !== 'owner' && (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => {
                                    setEditingMember(member);
                                    setEditRole(member.role);
                                    setEditOrgRole(member.org_role || '');
                                    setEditBillingEnabled(!!member.billing_enabled);
                                    setEditBillingRate(
                                      member.billing_rate != null ? String(member.billing_rate) : ''
                                    );
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors"
                                  title="Edit role &amp; billing"
                                >
                                  <PencilSquareIcon className="h-4.5 w-4.5" />
                                </button>
                                <button
                                  onClick={() => setDeletingMember(member)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                                  title="Remove member"
                                >
                                  <TrashIcon className="h-4.5 w-4.5" />
                                </button>
                              </div>
                            )}
                            {member.role === 'owner' && (
                              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Owner</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="p-4">
            {invitations.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <EnvelopeIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No invitations sent yet.</p>
                {isOrgAdmin && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Send your first invitation
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {invitations.map(inv => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-750 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center">
                      <div className={`w-2.5 h-2.5 rounded-full mr-3 ${
                        inv.status === 'pending' ? 'bg-amber-400' :
                        inv.status === 'accepted' ? 'bg-green-400' :
                        'bg-gray-400'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{inv.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Role: {inv.role} &middot; Status: {inv.status}
                          {inv.created_at && (
                            <> &middot; Sent: {new Date(inv.created_at).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    </div>
                    {inv.status === 'pending' && isOrgAdmin && (
                      <button
                        onClick={() => handleRevokeInvitation(inv.id)}
                        className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* INVITE MODAL */}
      {/* ═══════════════════════════════════════════════════ */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowInviteModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="bg-primary-100 dark:bg-primary-900/30 rounded-lg p-2 mr-3">
                    <UserPlusIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Invite Team Member
                  </h3>
                </div>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleInviteSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={inviteForm.first_name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={inviteForm.last_name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Moyo"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      required
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="colleague@firm.co.zw"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    System Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {orgRoles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization Role
                    </label>
                    <select
                      value={inviteForm.org_role}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, org_role: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Auto-assign based on role</option>
                      {orgRoles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Organization roles define specific permissions within the firm.
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    An invitation email will be sent with a link to join your organization. The invite expires in 7 days.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
                  >
                    {inviteLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4 mr-1.5" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* EDIT ROLE MODAL */}
      {/* ═══════════════════════════════════════════════════ */}
      {editingMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setEditingMember(null)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Edit Member — {editingMember.first_name} {editingMember.last_name}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    System Role
                  </label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {orgRoles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization Role
                    </label>
                    <select
                      value={editOrgRole}
                      onChange={(e) => setEditOrgRole(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">No change</option>
                      {orgRoles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Client billing settings (firm → client invoicing) */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-start">
                    <input
                      id="member_billing_enabled"
                      type="checkbox"
                      checked={editBillingEnabled}
                      onChange={(e) => setEditBillingEnabled(e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor="member_billing_enabled"
                      className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Can log billable time / be billed to clients
                    </label>
                  </div>

                  <div className="mt-3">
                    <label
                      htmlFor="member_billing_rate"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Default hourly rate
                    </label>
                    <input
                      id="member_billing_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editBillingRate}
                      onChange={(e) => setEditBillingRate(e.target.value)}
                      disabled={!editBillingEnabled}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Used as this member&rsquo;s default rate when logging time billed to clients.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateMemberRole}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ═══════════════════════════════════════════════════ */}
      {deletingMember && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setDeletingMember(null)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Remove Team Member
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Are you sure you want to remove <strong>{deletingMember.first_name} {deletingMember.last_name}</strong> from your organization? They will lose access to all firm resources.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDeletingMember(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveMember}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Remove Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* ADD USER DIRECTLY MODAL */}
      {/* ═══════════════════════════════════════════════════ */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowAddUserModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 mr-3">
                    <UserPlusIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Add User Directly
                  </h3>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAddUserSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={addUserForm.first_name}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={addUserForm.last_name}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Moyo"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      required
                      value={addUserForm.email}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, email: e.target.value }))}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="colleague@firm.co.zw"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={addUserForm.phone}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="+263 77 123 4567"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      System Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={addUserForm.role}
                      onChange={(e) => setAddUserForm(prev => ({ ...prev, role: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  {orgRoles.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization Role
                      </label>
                      <select
                        value={addUserForm.org_role}
                        onChange={(e) => setAddUserForm(prev => ({ ...prev, org_role: e.target.value }))}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Auto-assign</option>
                        {orgRoles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="text"
                    value={addUserForm.password}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Leave blank to auto-generate"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    If blank, a secure password will be generated. You can share it with the user or they can use &ldquo;Forgot Password&rdquo;.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-xs text-green-700 dark:text-green-300">
                    This will create an active user account immediately. No invitation email is sent — share the credentials directly.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addUserLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
                  >
                    {addUserLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <UserPlusIcon className="h-4 w-4 mr-1.5" />
                        Add User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* CREATED USER CREDENTIALS MODAL */}
      {/* ═══════════════════════════════════════════════════ */}
      {createdUserInfo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6">
              <div className="text-center mb-4">
                <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-3">
                  <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  User Created Successfully
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Share these credentials with <strong>{createdUserInfo.name}</strong>
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</p>
                  <p className="text-sm font-mono text-gray-900 dark:text-white mt-0.5">{createdUserInfo.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Temporary Password</p>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-sm font-mono text-gray-900 dark:text-white">{createdUserInfo.password}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdUserInfo.password);
                        setSuccess('Password copied to clipboard!');
                      }}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium ml-2"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Save this password now — it won't be shown again. The user can change it after logging in or use &ldquo;Forgot Password&rdquo;.
                </p>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setCreatedUserInfo(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
